import { useCallback, useEffect, useRef } from 'react';

interface IdleTimerProps {
  timeout: number;
  onIdle: () => Promise<void>;
  onActive?: () => void;
  heartbeat?: {
    timeout: number;
    onUpdate: () => Promise<boolean>;
    onCheck: () => Promise<boolean>;
  };
  throttle?: number;
}

/*
const numberToTime = (ticks: number) => {
  const date = new Date(ticks);
  return date.toLocaleTimeString('nl-NL', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}; */

const events = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'click',
];

const useIdleTimer = ({
  timeout,
  onIdle,
  onActive,
  heartbeat,
  throttle = 500,
}: IdleTimerProps) => {
  const updateTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callbackInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const isRunning = useRef(false);
  const isIdle = useRef(false);
  const lastCall = useRef<number>(0);
  const locking = useRef(false);
  const lastActivity = useRef<number>(Date.now());
  const eventHandler = useRef<(() => void) | null>(null);

  const throttledOnActive = useCallback(() => {
    const now = Date.now();
    if (now - lastCall.current >= throttle) {
      lastCall.current = now;
      onActive?.();
    }
  }, [onActive, throttle]);

  const stop = useCallback(() => {
    if (locking.current || !isRunning.current) return;
    // console.log(`[IDLE TIMER] STOP ${numberToTime(Date.now())}`);

    // Reset values
    isRunning.current = false;
    isIdle.current = true;

    // Clear the timeout
    if (updateTimeout.current) {
      clearTimeout(updateTimeout.current);
      updateTimeout.current = null;
    }

    // Clear the interval
    if (callbackInterval.current) {
      clearInterval(callbackInterval.current);
      callbackInterval.current = null;
    }

    // Remove event listeners
    events.forEach(event => {
      window.removeEventListener(event, eventHandler.current!);
    });
  }, [isRunning]);

  // wrap stop around lock
  const lock = useCallback(async () => {
    // console.log(`[IDLE TIMER] LOCK ${numberToTime(Date.now())}`);
    stop();
    await onIdle();
  }, [onIdle, stop]);

  useEffect(() => {
    if (!locking.current && isRunning.current && heartbeat && !isIdle.current) {
      if (callbackInterval.current) {
        clearInterval(callbackInterval.current);
      }
      callbackInterval.current = setInterval(async () => {
        const now = Date.now();
        if (now - lastActivity.current < heartbeat.timeout) {
          // console.log(`[IDLE TIMER] heartbeat UPDATE ${numberToTime(Date.now())}`);
          const result = await heartbeat.onUpdate();
          if (!result) {
            locking.current = true;
            await lock();
            locking.current = false;
          }
        } else {
          // console.log(`[IDLE TIMER] heartbeat CHECK ${numberToTime(Date.now())}`);
          const result = await heartbeat.onCheck();
          if (!result) {
            locking.current = true;
            await lock();
            locking.current = false;
          }
        }
      }, heartbeat.timeout);

      return () => {
        if (callbackInterval.current) {
          clearInterval(callbackInterval.current);
        }
      };
    }
    return undefined;
  }, [isRunning, heartbeat, isIdle, lock]);

  const update = useCallback(() => {
    if (locking.current || !isRunning.current || isIdle.current) return;

    // Clear the timeout
    if (updateTimeout.current) {
      clearTimeout(updateTimeout.current);
    }

    // Update the last activity time
    lastActivity.current = Date.now();
    throttledOnActive();

    updateTimeout.current = setTimeout(() => {
      const timeSinceLastActivity = Date.now() - lastActivity.current;
      if (timeSinceLastActivity >= timeout) {
        // setIsIdle(true);
        isIdle.current = true;
        lock();
      }
    }, timeout);
  }, [lock, throttledOnActive, timeout]);

  const start = useCallback(async () => {
    if (locking.current || isRunning.current) return;
    // console.log(`[IDLE TIMER] START ${numberToTime(Date.now())}`);

    eventHandler.current = () => update();

    isRunning.current = true;
    isIdle.current = false;

    // Clear the timeout
    if (updateTimeout.current) {
      clearTimeout(updateTimeout.current);
    }

    events.forEach(event => {
      window.addEventListener(event, eventHandler.current!, { passive: true });
    });

    // fire heartbeat
    if (heartbeat) {
      const result = await heartbeat.onUpdate();
      if (!result) {
        lock();
      }
    }

    // Start the timer
    update();
  }, [heartbeat, lock, update]);

  return {
    start,
    stop,
    isRunning: isRunning.current,
    isIdle: isIdle.current,
    locking: locking.current,
    lock,
  };
};

export { useIdleTimer };
